import React, { useState } from 'react';
import { C, FONT_BODY, CARD_STYLE } from '../theme';
import { supabase } from '../supabase';
import { saveSettings, _globalSettings } from '../constants';
import { SchedaDetailSheet } from '../components/Session';
import { saveDB } from '../database';

const TOTAL_STEPS = 5; // 0‥4

// ─── helpers ─────────────────────────────────────────────────────────────────
const GRAY_IN = {
  width: '100%', border: 'none', borderRadius: 14, padding: '15px 16px',
  background: '#F2F2F7', outline: 'none', boxSizing: 'border-box',
  fontFamily: FONT_BODY, fontSize: 16, color: '#000', WebkitAppearance: 'none',
};

function Row({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, marginBottom: 6, paddingLeft: 2 }}>{label}</div>
      {children}
    </div>
  );
}

function SegRow({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {options.map(o => {
        const sel = value === o.value;
        return (
          <button key={o.value} onClick={() => onChange(o.value)} style={{
            flex: 1, padding: '12px 6px', borderRadius: 14, border: 'none', cursor: 'pointer',
            fontFamily: FONT_BODY, fontSize: 13, fontWeight: sel ? 700 : 500,
            background: sel ? C.blue : '#F2F2F7', color: sel ? '#fff' : C.muted,
            transition: 'all 0.15s',
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

function ObjCard({ id, icon, title, sub, selected, onPress }) {
  return (
    <button onClick={() => onPress(id)} style={{
      flex: 1, padding: '16px 10px', borderRadius: 18, border: 'none', cursor: 'pointer',
      background: selected ? `${C.blue}0F` : '#F2F2F7',
      boxShadow: selected ? `0 0 0 2px ${C.blue}` : '0 0 0 1.5px transparent',
      fontFamily: FONT_BODY, textAlign: 'center', transition: 'all 0.15s',
    }}>
      <div style={{ fontSize: 26, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: selected ? C.blue : C.text, marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 11, color: C.subtle, lineHeight: 1.4 }}>{sub}</div>
    </button>
  );
}

// ─── REPLAY MODE (solo slide informative, nessun input) ──────────────────────
const REPLAY_SLIDES = [
  {
    icon: '👋',
    title: 'Come funziona GP',
    body: 'GP traccia ogni sessione e usa i tuoi dati reali per calcolare progressioni, volume e suggerimenti personalizzati.',
    tips: [
      { icon: '📊', text: 'Ogni sessione alimenta le analisi di progressione' },
      { icon: '🤖', text: 'Il coach calcola i carichi ottimali per la prossima sessione' },
      { icon: '📈', text: 'Confronti automatici tra sessioni dello stesso workout' },
    ],
  },
  {
    icon: '📁',
    title: 'Le schede',
    body: 'Ogni scheda è un piano di allenamento (es. "Petto", "Gambe e spalle"). GP le usa per raggruppare la cronologia e suggerire l\'allenamento del giorno.',
    tips: [
      { icon: '🎯', text: 'Il Coach widget mostra quale scheda è più "scaduta"' },
      { icon: '⚡', text: 'Carica sessione pre-compila kg e reps dall\'ultima volta' },
      { icon: '✏️', text: 'Modifica le schede dalla tab Esercizi in qualsiasi momento' },
    ],
  },
  {
    icon: '🏆',
    title: 'Pronto a continuare',
    body: 'Registra ogni allenamento — anche solo 2 sessioni sono sufficienti per avere le prime progressioni suggerite.',
    tips: [
      { icon: '➕', text: 'Premi + per registrare una sessione manuale' },
      { icon: '📥', text: 'Importa la cronologia da Hevy via CSV in Impostazioni' },
      { icon: '🔥', text: 'Dopo 3 sessioni ravvicinate appare la streak nella Home' },
    ],
  },
];

function ReplayOnboarding({ onComplete }) {
  const [step, setStep] = useState(0);
  const slide = REPLAY_SLIDES[step];
  const isLast = step === REPLAY_SLIDES.length - 1;
  const progressPct = (step / (REPLAY_SLIDES.length - 1)) * 100;

  return (
    <div style={{ fontFamily: FONT_BODY, background: '#F5F2ED', minHeight: '100vh', WebkitFontSmoothing: 'antialiased', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, background: 'rgba(0,0,0,0.06)', zIndex: 10 }}>
        <div style={{ height: '100%', background: C.blue, borderRadius: 2, width: `${progressPct}%`, transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)' }} />
      </div>
      {step > 0 && (
        <button onClick={() => setStep(s => s - 1)} style={{ position: 'fixed', top: 'max(16px, env(safe-area-inset-top, 16px))', left: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 8, zIndex: 10 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.text} strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
      )}
      <div style={{ position: 'fixed', top: 'max(18px, env(safe-area-inset-top, 18px))', right: 20, fontSize: 12, fontWeight: 600, color: C.subtle, zIndex: 10 }}>
        {step + 1} / {REPLAY_SLIDES.length}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 'max(72px, calc(env(safe-area-inset-top, 0px) + 60px)) 24px max(40px, calc(env(safe-area-inset-bottom, 0px) + 100px))', maxWidth: 480, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ fontSize: 56, marginBottom: 16, lineHeight: 1 }}>{slide.icon}</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: -0.6, margin: '0 0 10px' }}>{slide.title}</h1>
          <p style={{ fontSize: 15, color: C.subtle, margin: 0, lineHeight: 1.65 }}>{slide.body}</p>
        </div>
        <div style={{ ...CARD_STYLE, padding: '16px 18px' }}>
          {slide.tips.map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: i < slide.tips.length - 1 ? 12 : 0 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>{t.icon}</span>
              <span style={{ fontSize: 13, color: C.text, lineHeight: 1.45 }}>{t.text}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '16px 24px max(24px, calc(env(safe-area-inset-bottom, 0px) + 16px))', background: 'linear-gradient(to top, #F5F2ED 60%, transparent)' }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <button onClick={() => isLast ? onComplete() : setStep(s => s + 1)} style={{ width: '100%', padding: '16px', borderRadius: 16, border: 'none', background: C.blue, color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY, boxShadow: `0 4px 20px ${C.blue}40` }}>
            {isLast ? 'Chiudi' : 'Continua'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
export function OnboardingPage({ user, db, setDB, onComplete, replayMode }) {
  if (replayMode) return <ReplayOnboarding onComplete={onComplete} />;
  return <OnboardingFull user={user} db={db} setDB={setDB} onComplete={onComplete} />;
}

function OnboardingFull({ user, db, setDB, onComplete }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [, setSchedaDone] = useState(false);
  const [showSchedaSheet, setShowSchedaSheet] = useState(false);

  const [profile, setProfile] = useState({
    displayName: '',
    username: '',
    peso: '',
    altezza: '',
    eta: '',
    sesso: 'M',
    obiettivo: 'recomp',
    esperienza: 'intermedio',
    livelloAttivita: '1.55',
  });

  const up = (key, val) => setProfile(p => ({ ...p, [key]: val }));

  const goNext = async () => {
    setErr('');
    setBusy(true);
    try {
      if (step === 0) {
        if (!profile.displayName.trim()) { setErr('Inserisci il tuo nome'); return; }
        if (profile.username && !/^[a-z0-9_]{3,20}$/.test(profile.username)) {
          setErr('Username: 3-20 caratteri, solo lettere minuscole, numeri e _'); return;
        }
        await supabase.from('profiles').update({
          display_name: profile.displayName.trim(),
          ...(profile.username ? { username: profile.username.toLowerCase() } : {}),
        }).eq('user_id', user.id);
      }
      if (step === 2) {
        const newSettings = {
          ..._globalSettings,
          profilo: {
            ..._globalSettings.profilo,
            peso: profile.peso,
            altezza: profile.altezza,
            eta: profile.eta,
            sesso: profile.sesso,
            obiettivo: profile.obiettivo,
            esperienza: profile.esperienza,
            livelloAttivita: profile.livelloAttivita,
          },
        };
        saveSettings(newSettings);
      }
      if (step === TOTAL_STEPS - 1) { onComplete(); return; }
      setStep(s => s + 1);
    } finally { setBusy(false); }
  };

  const goBack = () => { setStep(s => s - 1); setErr(''); };

  const canNext = () => {
    if (step === 0) return profile.displayName.trim().length > 0;
    if (step === 1) return parseFloat(profile.peso) > 0 && parseFloat(profile.altezza) > 0 && parseFloat(profile.eta) > 0;
    return true;
  };

  const handleSchedaSave = (updatedDb) => {
    setDB(updatedDb);
    saveDB(updatedDb);
    setSchedulaDone(true);
    setShowSchedaSheet(false);
  };

  // Fix typo in above
  const setSchedulaDone = setSchedaDone;

  const progressPct = ((step) / (TOTAL_STEPS - 1)) * 100;

  return (
    <div style={{
      fontFamily: FONT_BODY, background: '#F5F2ED', minHeight: '100vh',
      WebkitFontSmoothing: 'antialiased', display: 'flex', flexDirection: 'column',
    }}>
      {/* Progress bar */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, background: 'rgba(0,0,0,0.06)', zIndex: 10 }}>
        <div style={{
          height: '100%', background: C.blue, borderRadius: 2,
          width: `${progressPct}%`, transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>

      {/* Back nav */}
      {step > 0 && step < TOTAL_STEPS - 1 && (
        <button onClick={goBack} style={{
          position: 'fixed', top: 'max(16px, env(safe-area-inset-top, 16px))', left: 20,
          background: 'none', border: 'none', cursor: 'pointer', padding: 8, zIndex: 10,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.text} strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
      )}

      {/* Step counter */}
      {step < TOTAL_STEPS - 1 && (
        <div style={{
          position: 'fixed', top: 'max(18px, env(safe-area-inset-top, 18px))', right: 20,
          fontSize: 12, fontWeight: 600, color: C.subtle, zIndex: 10,
        }}>
          {step + 1} / {TOTAL_STEPS}
        </div>
      )}

      {/* Content */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: 'max(72px, calc(env(safe-area-inset-top, 0px) + 60px)) 24px max(40px, calc(env(safe-area-inset-bottom, 0px) + 100px))',
        maxWidth: 480, margin: '0 auto', width: '100%', boxSizing: 'border-box',
      }}>

        {/* ── STEP 0: Chi sei ── */}
        {step === 0 && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>👋</div>
              <h1 style={{ fontSize: 30, fontWeight: 800, color: C.text, letterSpacing: -0.8, margin: '0 0 10px' }}>Benvenuto in GP</h1>
              <p style={{ fontSize: 16, color: C.subtle, margin: 0, lineHeight: 1.6 }}>Inizia col dirci come ti chiami.</p>
            </div>
            <Row label="Nome e cognome">
              <input
                value={profile.displayName}
                onChange={e => up('displayName', e.target.value)}
                placeholder="es. Marco Rossi"
                style={GRAY_IN}
                autoFocus
              />
            </Row>
            <Row label="Username (opzionale)">
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.subtle, fontSize: 16, pointerEvents: 'none' }}>@</span>
                <input
                  value={profile.username}
                  onChange={e => up('username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="tuousername"
                  style={{ ...GRAY_IN, paddingLeft: 30 }}
                />
              </div>
              <div style={{ fontSize: 12, color: C.subtle, marginTop: 6, paddingLeft: 2 }}>
                Gli amici ti troveranno con @username
              </div>
            </Row>
          </div>
        )}

        {/* ── STEP 1: Dati fisici ── */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: -0.6, margin: '0 0 8px' }}>Il tuo corpo</h1>
              <p style={{ fontSize: 15, color: C.subtle, margin: 0, lineHeight: 1.6 }}>Questi dati servono per calcolare TDEE, progressioni e obiettivi.</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <Row label="Peso (kg)">
                <input type="number" value={profile.peso} onChange={e => up('peso', e.target.value)}
                  placeholder="75" style={GRAY_IN} inputMode="decimal" />
              </Row>
              <Row label="Altezza (cm)">
                <input type="number" value={profile.altezza} onChange={e => up('altezza', e.target.value)}
                  placeholder="175" style={GRAY_IN} inputMode="numeric" />
              </Row>
            </div>
            <Row label="Età">
              <input type="number" value={profile.eta} onChange={e => up('eta', e.target.value)}
                placeholder="25" style={GRAY_IN} inputMode="numeric" />
            </Row>
            <Row label="Sesso">
              <SegRow
                options={[{ value: 'M', label: 'Maschio' }, { value: 'F', label: 'Femmina' }]}
                value={profile.sesso}
                onChange={v => up('sesso', v)}
              />
            </Row>
          </div>
        )}

        {/* ── STEP 2: Obiettivo ── */}
        {step === 2 && (
          <div>
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: -0.6, margin: '0 0 8px' }}>Il tuo obiettivo</h1>
              <p style={{ fontSize: 15, color: C.subtle, margin: 0, lineHeight: 1.6 }}>Personalizzeremo i suggerimenti in base a questo.</p>
            </div>
            <Row label="Cosa vuoi ottenere?">
              <div style={{ display: 'flex', gap: 10 }}>
                <ObjCard id="bulk" icon="💪" title="Massa" sub="Aumenta la muscolatura" selected={profile.obiettivo === 'bulk'} onPress={v => up('obiettivo', v)} />
                <ObjCard id="recomp" icon="⚖️" title="Recomp" sub="Forma e forza" selected={profile.obiettivo === 'recomp'} onPress={v => up('obiettivo', v)} />
                <ObjCard id="cut" icon="🔥" title="Cut" sub="Definisci e dimagrisce" selected={profile.obiettivo === 'cut'} onPress={v => up('obiettivo', v)} />
              </div>
            </Row>
            <Row label="Esperienza in palestra">
              <SegRow
                options={[
                  { value: 'principiante', label: 'Principiante' },
                  { value: 'intermedio', label: 'Intermedio' },
                  { value: 'avanzato', label: 'Avanzato' },
                ]}
                value={profile.esperienza}
                onChange={v => up('esperienza', v)}
              />
            </Row>
            <Row label="Livello di attività quotidiana">
              <SegRow
                options={[
                  { value: '1.2', label: 'Sedentario' },
                  { value: '1.55', label: 'Moderato' },
                  { value: '1.725', label: 'Molto attivo' },
                ]}
                value={profile.livelloAttivita}
                onChange={v => up('livelloAttivita', v)}
              />
            </Row>
          </div>
        )}

        {/* ── STEP 3: Schede ── */}
        {step === 3 && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: -0.6, margin: '0 0 8px' }}>Le tue schede</h1>
              <p style={{ fontSize: 15, color: C.subtle, margin: 0, lineHeight: 1.6 }}>
                Ogni scheda è un piano di allenamento (es. "Petto", "Gambe"). Aggiungine quante vuoi — puoi sempre modificarle dopo.
              </p>
            </div>

            {/* Value props — only when no schede yet */}
            {db.schede?.length === 0 && (
              <div style={{ ...CARD_STYLE, padding: '16px 18px', marginBottom: 16 }}>
                {[
                  { icon: '📊', text: 'Progressioni suggerite ad ogni sessione' },
                  { icon: '🎯', text: 'Coach widget con il prossimo allenamento' },
                  { icon: '📈', text: 'Confronti tra sessioni dello stesso workout' },
                ].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < 2 ? 10 : 0 }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{f.icon}</span>
                    <span style={{ fontSize: 13, color: C.text, lineHeight: 1.4 }}>{f.text}</span>
                  </div>
                ))}
              </div>
            )}

            {/* List of created schede */}
            {db.schede?.length > 0 && (
              <div style={{ ...CARD_STYLE, padding: 0, marginBottom: 12, overflow: 'hidden' }}>
                {db.schede.map((s, i) => (
                  <div key={s.id}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: `${C.blue}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="6" y1="12" x2="18" y2="12"/><line x1="2" y1="12" x2="4" y2="12"/>
                          <line x1="20" y1="12" x2="22" y2="12"/>
                          <rect x="4" y="10" width="2" height="4" rx="1"/><rect x="18" y="10" width="2" height="4" rx="1"/>
                        </svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{s.name}</div>
                        <div style={{ fontSize: 12, color: C.subtle }}>{s.exercises?.length || 0} esercizi</div>
                      </div>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    {i < db.schede.length - 1 && <div style={{ height: 1, background: 'rgba(60,60,67,0.10)', margin: '0 16px' }}/>}
                  </div>
                ))}
              </div>
            )}

            {/* Add scheda button */}
            <button onClick={() => setShowSchedaSheet(true)} style={{
              width: '100%', padding: '14px', borderRadius: 14,
              border: `1.5px dashed ${db.schede?.length > 0 ? 'rgba(60,60,67,0.18)' : C.blue}`,
              background: db.schede?.length > 0 ? 'none' : `${C.blue}08`,
              color: db.schede?.length > 0 ? C.blue : C.blue,
              fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: FONT_BODY, marginBottom: 10,
            }}>
              + {db.schede?.length > 0 ? 'Aggiungi un\'altra scheda' : 'Carica le mie schede'}
            </button>

            {/* Skip — only when no schede */}
            {db.schede?.length === 0 && (
              <button onClick={() => setStep(TOTAL_STEPS - 1)} style={{
                width: '100%', padding: '10px', background: 'none', border: 'none',
                color: C.subtle, fontSize: 13, cursor: 'pointer', fontFamily: FONT_BODY,
              }}>
                Salta per ora (sconsigliato)
              </button>
            )}
          </div>
        )}

        {/* ── STEP 4: Done ── */}
        {step === TOTAL_STEPS - 1 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 72, marginBottom: 24, lineHeight: 1 }}>🏆</div>
            <h1 style={{ fontSize: 32, fontWeight: 800, color: C.text, letterSpacing: -0.8, margin: '0 0 14px' }}>
              Tutto pronto,<br />{profile.displayName.split(' ')[0]}!
            </h1>
            <p style={{ fontSize: 16, color: C.subtle, margin: '0 0 40px', lineHeight: 1.7 }}>
              Il tuo profilo è configurato.<br />Inizia a registrare i tuoi allenamenti.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
              {[
                { text: 'Premi + per registrare il primo allenamento', icon: '➕' },
                { text: 'Ogni sessione alimenta le tue progressioni', icon: '📈' },
                { text: 'Il coach ti suggerirà i carichi ad ogni sessione', icon: '🤖' },
              ].map((h, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.7)', borderRadius: 12, padding: '12px 16px', width: '100%', boxSizing: 'border-box' }}>
                  <span style={{ fontSize: 18 }}>{h.icon}</span>
                  <span style={{ fontSize: 14, color: C.text, textAlign: 'left', lineHeight: 1.4 }}>{h.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {err && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: `${C.red}12`, borderRadius: 10, fontSize: 13, color: C.red }}>
            {err}
          </div>
        )}
      </div>

      {/* CTA button — fixed bottom (not shown on step 3 when scheda not done) */}
      {(step !== 3 || (db.schede?.length ?? 0) > 0) && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          padding: `16px 24px max(24px, calc(env(safe-area-inset-bottom, 0px) + 16px))`,
          background: 'linear-gradient(to top, #F5F2ED 60%, transparent)',
        }}>
          <div style={{ maxWidth: 480, margin: '0 auto' }}>
            <button
              onClick={goNext}
              disabled={!canNext() || busy}
              style={{
                width: '100%', padding: '16px', borderRadius: 16, border: 'none',
                background: canNext() ? C.blue : 'rgba(120,120,128,0.12)',
                color: canNext() ? '#fff' : C.subtle,
                fontSize: 16, fontWeight: 700, cursor: canNext() ? 'pointer' : 'default',
                fontFamily: FONT_BODY, transition: 'all 0.15s',
                boxShadow: canNext() ? `0 4px 20px ${C.blue}40` : 'none',
              }}
            >
              {busy ? '…' : step === TOTAL_STEPS - 1 ? 'Inizia il percorso 🚀' : 'Continua'}
            </button>
          </div>
        </div>
      )}

      {/* Scheda sheet */}
      {showSchedaSheet && (
        <SchedaDetailSheet
          scheda={null}
          db={db}
          onSave={handleSchedaSave}
          onClose={() => setShowSchedaSheet(false)}
        />
      )}
    </div>
  );
}
